import {
  TrendingUp,
  Calculator,
  Building2,
  Users2,
  MapPin,
} from "lucide-react";
import type { BlogPost } from "../types";

export const rentVsSellGuide: BlogPost = {
  slug: "rent-vs-sell-guide",
  title: "Rent vs. Sell Your Home: A Guide for Property Owners",
  excerpt:
    "Should you rent your home or sell it? A practical guide to evaluating the numbers, your goals, and the responsibilities of becoming a landlord in Maryland, DC, and Northern Virginia.",
  date: "Sept 2, 2026",
  category: "Investment Strategy",
  readTime: "5 min read",
  author: {
    name: "Feingold Property Management Team",
    location: "Laurel, MD & Mid-Atlantic Region",
    initials: "FP",
  },
  intro: [
    "Should you rent your home or sell it?",
    "It's a question many homeowners face after relocating, inheriting property, purchasing another home, or deciding what to do with a property they no longer plan to occupy.",
    "For homeowners throughout Maryland, Washington DC, and Northern Virginia, there isn't one answer that works for everyone.",
    "The better approach is to evaluate the numbers, your long-term goals, the local rental market, and the responsibilities that come with becoming a landlord.",
  ],
  sections: [
    {
      number: "1",
      icon: TrendingUp,
      title: "Start With Potential Rental Income",
      paragraphs: [
        "First, determine what your home could realistically rent for.",
        "Don't base the decision on what you hope someone will pay. Research comparable rental properties in your neighborhood based on location, size, condition, amenities, parking, bedrooms, bathrooms, and other relevant factors.",
      ],
      callout: {
        text: "A local property management company can help evaluate market rent and provide a more realistic picture of potential rental income.",
        variant: "accent",
      },
    },
    {
      number: "2",
      icon: Calculator,
      title: "Calculate the Real Cost of Renting",
      paragraphs: [
        "Rental income isn't the same as rental profit.",
        "Property owners should consider mortgage payments, property taxes, insurance, maintenance, repairs, vacancy, property management fees, HOA expenses, and future capital improvements.",
      ],
      callout: {
        text: "For example, a property generating $3,000 per month in rent isn't necessarily producing $3,000 in monthly cash flow. Know the numbers before making the decision.",
        variant: "warning",
      },
    },
    {
      number: "3",
      icon: Building2,
      title: "Consider Long-Term Appreciation",
      paragraphs: [
        "Selling may provide access to equity today, while keeping the property allows you to retain ownership of the asset.",
        "That means owners should consider their long-term real estate strategy.",
        "Do you believe the property still fits your investment goals? Are you comfortable keeping capital tied up in it? Would selling allow you to use that equity more effectively somewhere else?",
      ],
      callout: {
        text: "These are investment questions, not simply property management questions.",
        variant: "accent",
      },
    },
    {
      number: "4",
      icon: Users2,
      title: "Decide Whether You Want to Be a Landlord",
      paragraphs: [
        "Owning rental property requires work.",
        "There are tenants to screen, leases to administer, rent to collect, repairs to coordinate, financial records to maintain, and applicable landlord-tenant regulations to follow.",
        "Some owners enjoy managing their own properties. Others prefer hiring a residential property management company in Maryland, DC, or Northern Virginia to handle day-to-day operations.",
      ],
    },
    {
      number: "5",
      icon: MapPin,
      title: "Look Beyond Today's Market",
      paragraphs: [
        "Real estate decisions should rarely be based on one month or one year.",
        "Consider where you expect to be financially and personally in five or ten years.",
      ],
      callout: {
        text: "Selling provides liquidity and removes the responsibility of owning the property. Renting can provide ongoing income while allowing you to retain the underlying real estate asset.",
        variant: "accent",
      },
    },
  ],
  conclusion: {
    title: "Rent or Sell? Start With the Numbers",
    paragraphs: [
      "Before making your decision, calculate realistic rental income, operating expenses, expected cash flow, available equity, and the costs associated with selling.",
      "Then consider your goals.",
    ],
    highlight:
      "The question isn't simply whether your home can be rented. It's whether keeping it as a rental property supports the financial future you're trying to build.",
  },
};
